import { useCallback, useEffect, useRef } from 'react';
import TooltipV2 from '@/components/TooltipV2';
import { saveClipChanges } from '@/stores/clip';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import { useBeforeUnload } from '@/hooks/useBeforeUnload';
import { getClipAnalyticsPayload } from '@/hooks/utils/analyticsHookUtil';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';

const DEFAULT_ICON_SIZE = 20;

// The save icon (cloud check / spinner / cloud cancel) with its develop tooltips always renders.
// `showLabel` additionally shows the save state as a pill beside it (for the top-nav status line);
// `size` lets that status line use a smaller icon that matches the pill text.
const ClipUpdateInProgress = ({
  clipId,
  showLabel = false,
  size = DEFAULT_ICON_SIZE
}: {
  clipId: string;
  showLabel?: boolean;
  size?: number;
}) => {
  const { status } = useClipPendingUpdate({ clipId });

  const shouldAttemptRetrySaving = useRef(false);
  shouldAttemptRetrySaving.current = status === ClipSyncStatus.sync_failed;

  const retrySavingBeforeUnload = useCallback(() => {
    saveClipChanges({
      clipId,
      saveSource: 'retry',
      onFailure: ({ error, clip }) => {
        window.logger?.error('LAST_SAVE_ATTEMPT_FAILED', error);
        window.analytics?.track('ContentLabEvent', {
          name: 'LAST_SAVE_ATTEMPT_FAILED',
          isSuccess: false,
          ...getClipAnalyticsPayload(clip)
        });
      }
    });
  }, [clipId]);

  useEffect(() => {
    return () => {
      if (shouldAttemptRetrySaving.current) {
        retrySavingBeforeUnload();
      }
    };
  }, [retrySavingBeforeUnload]);

  useBeforeUnload({ shouldPrompt: shouldAttemptRetrySaving.current, action: retrySavingBeforeUnload });

  const icon =
    status === ClipSyncStatus.sync_in_progress ? (
      <Icon name="IconLoader2" className="animate-spin" size={size} />
    ) : status === ClipSyncStatus.sync_failed ? (
      <TooltipV2
        tooltipId="edits-out-of-sync"
        tooltipContent="Changes not saved"
        tooltipElement={<Icon name="IconCloudCancel" color="red" size={size} />}
      />
    ) : (
      <TooltipV2
        tooltipId="edits-saved"
        tooltipContent="All changes saved"
        tooltipElement={<Icon name="IconCloudCheck" size={size} />}
      />
    );

  if (showLabel) {
    const isSaving = status === ClipSyncStatus.sync_in_progress;
    const isSaveFailed = status === ClipSyncStatus.sync_failed;
    return (
      <span className="flex items-center gap-1.5">
        <span className="flex shrink-0 items-center">{icon}</span>
        {isSaving || isSaveFailed ? (
          // Active/failed save states use the same pill treatment as "Publishing"/"Publishing Failed".
          <span
            data-testid="clip-save-status-label"
            className={classnames('flex size-fit items-center rounded-full px-2.5 py-1 text-2xs font-medium', {
              'bg-rose-200 text-rose-500': isSaveFailed,
              'bg-slate-100 text-slate-600': isSaving
            })}
          >
            {isSaving ? 'Saving…' : 'Changes not saved'}
          </span>
        ) : (
          // Settled state is plain text, matching "Last Published …".
          <span data-testid="clip-save-status-label" className="flex items-center text-2xs font-medium text-slate-500">
            All changes saved
          </span>
        )}
      </span>
    );
  }

  return <div className="z-[100] flex items-center">{icon}</div>;
};

export default ClipUpdateInProgress;
